import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getDailyInput } from '../services'
import { CustomerPipelineFilters } from '../types'
import dayjs from 'dayjs'
import { EmployeeContactData } from '../components/EmployeeContactsTable'

interface UseEmployeeContactsDataReturn {
    /** Employee data aggregated by sales_manager_code */
    employeeData: EmployeeContactData[]
    /** Previous period data keyed by sales_manager_code */
    previousEmployeeData: Record<number, number>
    /** Total contacts for current period */
    totalContacts: number
    /** Total contacts for previous period */
    previousTotalContacts: number
    /** Loading state */
    isLoading: boolean
    /** Error state */
    isError: boolean
}

export const useEmployeeContactsData = (
    filters: CustomerPipelineFilters
): UseEmployeeContactsDataReturn => {
    // Calculate previous period filters
    const previousFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null

        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const diffDays = end.diff(start, 'day') + 1

        return {
            ...filters,
            gte: start.subtract(diffDays, 'day').format('YYYY-MM-DD'),
            lte: start.subtract(1, 'day').format('YYYY-MM-DD'),
        }
    }, [filters])

    // Current period query
    const { data, isLoading, isError } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'daily-input',
                filters.gte,
                filters.lte,
                filters.sales_manager_codes,
            ],
            queryFn: () => getDailyInput(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {}
    )

    // Previous period query
    const { data: previousData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'daily-input',
                previousFilters?.gte,
                previousFilters?.lte,
                previousFilters?.sales_manager_codes,
            ],
            queryFn: () => getDailyInput(previousFilters!),
            enabled: !!previousFilters?.gte && !!previousFilters?.lte,
        },
        {}
    )

    // Aggregate by employee
    const employeeData = useMemo(() => {
        if (!data) return []

        const grouped = data.reduce(
            (acc, item) => {
                const key = item.sales_manager_code
                if (!acc[key]) {
                    acc[key] = {
                        sales_manager_code: key,
                        employee_name: item.employee_name,
                        new_customers_count: 0,
                    }
                }
                acc[key].new_customers_count += item.new_customers_count
                return acc
            },
            {} as Record<number, EmployeeContactData>
        )

        return Object.values(grouped).sort(
            (a, b) => b.new_customers_count - a.new_customers_count
        )
    }, [data])

    // Previous period employee data
    const previousEmployeeData = useMemo(() => {
        if (!previousData) return {}

        return previousData.reduce(
            (acc, item) => {
                const key = item.sales_manager_code
                if (!acc[key]) {
                    acc[key] = 0
                }
                acc[key] += item.new_customers_count
                return acc
            },
            {} as Record<number, number>
        )
    }, [previousData])

    // Totals
    const totalContacts = useMemo(() => {
        return employeeData.reduce(
            (sum, emp) => sum + emp.new_customers_count,
            0
        )
    }, [employeeData])

    const previousTotalContacts = useMemo(() => {
        return Object.values(previousEmployeeData).reduce(
            (sum, count) => sum + count,
            0
        )
    }, [previousEmployeeData])

    return {
        employeeData,
        previousEmployeeData,
        totalContacts,
        previousTotalContacts,
        isLoading,
        isError,
    }
}
